import {
  FoodDetailsBody,
  FoodDetailsContent,
  FoodDetailsHeader,
  FoodDetailsInfo,
  IngredientList,
  IngredientListTitle,
  StyledFoodDetails,
} from "./styled"
import { BackButton } from "components/Common/BackButton"
import Button from "components/Buttons/Button"
import { FoodListDivider } from "components/foodItem/FoodListItem/styled"
import FoodImage from "components/foodDetails/share/FoodImage"
import { TopNavWrapper } from "components/Layouts/TopNavWrapper"
import { FOOD_DETAILS_BODY_ID } from "config"
import { useState } from "react"
import ChevronLeftBtn from "components/Icons/ChevronLeftBtn"
import PopConfirm from "components/PopConfirm"
import useMyFoodDetailsForm from "features/food/hooks/myFood/useMyFoodDetailsForm"
import MyFoodNameAndPortion from "components/foodDetails/share/MyFoodNameAndPortion"
import { IAIScanMyFoodOption } from "model/food/types/myfood.types"
import { isFoodInfoChanged } from "features/food/helper/foodHelper"
import ViewOnlyIngredient from "components/foodDetails/share/ViewOnlyIngredient"
import AppStyleCtaBox from "components/Common/AppStyleCtaBox"
import EditableNutrientInformation from "components/foodDetails/share/EditableNutrientInformation"
import EditNutrientForm from "components/foodDetails/share/EditNutrientForm"

interface IFoodDetailsProps {
  onBack: () => void
  food: IAIScanMyFoodOption
  onEdit: (food: IAIScanMyFoodOption) => void
}

const MyFoodOptionDetails = (props: IFoodDetailsProps) => {
  const { onBack, food, onEdit } = props
  const { myFood, onPortionChange, onUnitChange, updateFoodAsManualNutrition } =
    useMyFoodDetailsForm(food)
  const [displaySection, setDisplaySection] = useState<
    "FOOD_FORM" | "EDIT_NUTRITION_FORM"
  >("FOOD_FORM")

  const [isPortionValid, setIsPortionValid] = useState(true)
  const [isPopConfirmDiscardOpen, setIsPopConfirmDiscardOpen] = useState(false)

  if (!myFood) {
    return null
  }

  const onPortionChangeHandler = (value: number) => {
    onPortionChange(value)
    setIsPortionValid(true)
  }

  const handleUpdateFood = () => {
    if (!myFood) return

    onEdit(myFood)
    onBack()
  }
  const isFoodChanged = () => {
    return isFoodInfoChanged(food, myFood)
  }

  if (displaySection === "EDIT_NUTRITION_FORM") {
    return (
      <EditNutrientForm
        onClose={() => {
          setDisplaySection("FOOD_FORM")
        }}
        meal={myFood}
        onSubmit={updateFoodAsManualNutrition}
      />
    )
  }

  return (
    <StyledFoodDetails>
      <PopConfirm
        isOpen={isPopConfirmDiscardOpen}
        title="Unsaved changes?"
        cancelText="Discard"
        confirmText="Save changes"
        onCancel={onBack}
        onConfirm={handleUpdateFood}
        loading={false}
      >
        <p style={{ textAlign: "center" }}>
          You haven't saved your meal yet. Are you sure you want to leave?
        </p>
      </PopConfirm>

      <FoodDetailsContent>
        <FoodDetailsBody id={FOOD_DETAILS_BODY_ID}>
          <FoodImage previewImageUrl={myFood.previewImageUrl}>
            <TopNavWrapper>
              <FoodDetailsHeader>
                <BackButton
                  onClick={() => {
                    if (isFoodChanged()) {
                      setIsPopConfirmDiscardOpen(true)
                    } else {
                      onBack()
                    }
                  }}
                >
                  <ChevronLeftBtn />
                </BackButton>
              </FoodDetailsHeader>
            </TopNavWrapper>
          </FoodImage>
          <FoodDetailsInfo>
            <MyFoodNameAndPortion
              food={myFood}
              unitValue={myFood.unit}
              portionValue={myFood.portion}
              onPortionChange={onPortionChangeHandler}
              onUnitChange={onUnitChange}
              setIsPortionValid={setIsPortionValid}
              calories={myFood.calorie}
              gram={myFood.gram}
            />

            <EditableNutrientInformation
              entry={myFood}
              setEditMode={() => {
                setDisplaySection("EDIT_NUTRITION_FORM")
              }}
            />

            <IngredientList>
              <IngredientListTitle>Ingredients</IngredientListTitle>{" "}
              {myFood.ingredients.map((ingredient, index) => (
                <div key={index}>
                  <ViewOnlyIngredient
                    ingredient={ingredient}
                    isManualNutrition={myFood.isManualNutrition}
                  />
                  {index !== (food.ingredients?.length ?? 1) - 1 && (
                    <FoodListDivider />
                  )}
                </div>
              ))}
            </IngredientList>
          </FoodDetailsInfo>
        </FoodDetailsBody>
      </FoodDetailsContent>

      <AppStyleCtaBox
        style={{
          marginTop: "auto",
          flexDirection: "row",
        }}
      >
        <Button
          style={{ width: "100%" }}
          $type="primary"
          onClick={handleUpdateFood}
          disabled={!isPortionValid}
        >
          Save
        </Button>
      </AppStyleCtaBox>
    </StyledFoodDetails>
  )
}

export default MyFoodOptionDetails
